import React, { useEffect, useState } from 'react'
import { OrdersetSettingsPageBusinessLogicProvider } from './context'
import { useHistory } from 'react-router'
import { SETTINGS } from '../../../../config/routes'
import {
  useMutationAddPartnerProjectPackageApi,
  useMutationCreatePackageApi,
  useMutationDeletePackageApi,
  useMutationDeletePartnerProjectPackageApi,
  useMutationUpdatePackageApi,
  useProvidersViewModel,
  useQueryGetPackageApi,
  useQueryListCompendiumPanelsApi,
  useQueryListPackageCategoriesApi,
  useUserViewModel,
} from '../../../../viewmodels'
import { CategoryPanelInput, Center, Loader, Package, PackageCategoryPanel, ProjectPackage, useDisclosure, useForm } from '@labsavvyapp/components'
import { generatePackageCategoryDropdownItems, generateCompendiaPanelDropdownItems, generateCategoryPanelInput } from '../utils'
import { useQueryListConfigsApi } from '../../../../viewmodels/settings'
import { useQuery } from 'react-apollo'
import { ListPartnerProjectPackages } from '../../../../graphql/packages/queries'

interface ComponentProps extends React.ComponentProps<any> {}

export const OrdersetSettingsPageLogic: React.FunctionComponent<ComponentProps> = (props: ComponentProps) => {
  const { push } = useHistory()

  const userViewModel = useUserViewModel()
  const me = userViewModel?.me ?? null
  const partnerId = me?.partner?._id ?? ''
  const projectId = me?.project?._id ?? ''

  useEffect(() => {
    refetchPackageList()
  }, [projectId])

  const providersViewModel = useProvidersViewModel()
  const providerListForDropdown = providersViewModel?.partnerProjectProvidersListForDropdown ?? []

  const [visible, { open: openLoadingModal, close: closeLoadingModal, toggle: toggleLoadingModal }] = useDisclosure(false)

  const [userSelectedLaboratoryId, setUserSelectedLaboratoryId] = useState<string>(providerListForDropdown[0]?.value)
  const [userSelectedOrdersetId, setUserSelectedOrdersetId] = useState<string>('')
  const [totalCreateOrdersetClientPrice, setTotalCreateOrdersetClientPrice] = useState<number>(0)
  const [totalCreateOrdersetPatientPrice, setTotalCreateOrdersetPatientPrice] = useState<number>(0)
  const [totalEditOrdersetClientPrice, setTotalEditOrdersetClientPrice] = useState<number>(0)
  const [totalEditOrdersetPatientPrice, setTotalEditOrdersetPatientPrice] = useState<number>(0)

  const initialCategories: CategoryPanelInput[] = []

  const createOrdersetForm = useForm({
    initialValues: {
      name: '',
      ls_price: 0,
      partner_price: 0,
      retail_price: 0,
      categories: initialCategories,
      provider_id: userSelectedLaboratoryId,
      is_private: true,
    },

    validate: {
      name: (value) => (value?.length <= 0 ? 'Cannot be empty' : null),
      categories: (value) => (value?.length <= 0 ? 'Cannot be empty' : null),
    },
  })

  const editOrdersetForm = useForm({
    initialValues: {
      name: '',
      ls_price: 0,
      partner_price: 0,
      retail_price: 0,
      categories: initialCategories,
      provider_id: userSelectedLaboratoryId,
    },

    validate: {
      name: (value) => (value?.length <= 0 ? 'Cannot be empty' : null),
      categories: (value) => (value?.length <= 0 ? 'Cannot be empty' : null),
    },
  })

  const { data: listCompendiaPanelsData } = useQueryListCompendiumPanelsApi({
    providerId: userSelectedLaboratoryId,
    type: 'group',
  })
  const { data: defaultCategoryData } = useQueryListConfigsApi({
    filter: {
      domain: 'SYS',
      category: 'DEFAULT',
      code: 'SYSDEF001',
    },
  })
  const { data: listPackageCategoriesData } = useQueryListPackageCategoriesApi({ limit: 100 })
  const {
    data: userSelectedOrdersetData,
    loading: userSelectedOrdersetDataLoading,
    refetch: refetchUserSelectedOrdersetData,
  } = useQueryGetPackageApi({ id: userSelectedOrdersetId })

  // TODO: skip not working properly - Apollo client issue on current version https://github.com/apollographql/apollo-client/issues/9765. Will be fixed once apollo client version updated, no need for fallback and we can rely on skip if params are not yet ready.

  const {
    data: listPartnerProjectPackagesData,
    loading: listPartnerProjectPackagesDataLoading,
    refetch: refetchPackageList,
  } = useQuery(ListPartnerProjectPackages, {
    variables: {
      partnerId: me?.partner._id ?? '611c1464f1e00f0018e82600',
      projectId: me?.project._id ?? '611c1752f1e00f0018e83167',
      providerId: userSelectedLaboratoryId ?? '5ca399c8a8851500257379f3',
    },
    skip: !me || !userSelectedLaboratoryId,
  })

  // Dropdown options
  const packageCategoriesDropdownItems = generatePackageCategoryDropdownItems(listPackageCategoriesData?.listPackageCategories?.package_categories)
  const compendiumPanelDropdownItems = generateCompendiaPanelDropdownItems(listCompendiaPanelsData?.compendium_panels)

  // Mutations
  const [createPackage, { data: createPackageResult, loading: createPackageLoading }] = useMutationCreatePackageApi()
  const [updatePackage, { loading: updatePackageLoading }] = useMutationUpdatePackageApi()
  const [deletePackage, { loading: deletePackageLoading }] = useMutationDeletePackageApi()
  const [addPartnerProjectPackage, { loading: addPartnerProjectPackageLoading }] = useMutationAddPartnerProjectPackageApi()
  const [deletePartnerProjectPackage, { loading: deletePartnerProjectPackageLoading }] = useMutationDeletePartnerProjectPackageApi()

  // Preselect laboratory
  useEffect(() => {
    if (providerListForDropdown.length > 0) {
      handleUserSelectedLaboratoryIdChange(providerListForDropdown[0].value)
    }
  }, [providerListForDropdown])

  // Add orderset to partner project after orderset creation
  useEffect(() => {
    if (createPackageResult && !createPackageLoading) {
      const createdPackage = createPackageResult.createPackage as Package

      const addPartnerProjectPackagePayload = {
        partnerId: me?.partner?._id,
        projectId: me?.project?._id,
        packageId: createdPackage?._id,
        providerId: userSelectedLaboratoryId,
        name: createdPackage?.name,
        commission: 0,
      }

      addPartnerProjectPackage({
        variables: addPartnerProjectPackagePayload,
      })

      openLoadingModal()

      setTimeout(() => {
        closeLoadingModal()
      }, 2000)
      setTimeout(() => {
        goToOrdersetListPage()
        createOrdersetForm.reset()
      }, 1000)
    }
  }, [createPackageResult, createPackageLoading])

  // Update edit orderset values
  useEffect(() => {
    editOrdersetForm.setValues({
      name: userSelectedOrdersetData?.name,
      ls_price: userSelectedOrdersetData?.ls_price,
      partner_price: userSelectedOrdersetData?.partner_price,
      retail_price: userSelectedOrdersetData?.retail_price,
      categories: generateCategoryPanelInput(userSelectedOrdersetData?.categories, defaultCategoryData?.configs[0]?.value),
    })
  }, [userSelectedOrdersetData])

  const handleUserSelectedLaboratoryIdChange = (updatedLaboratoryId: string) => {
    setUserSelectedLaboratoryId(updatedLaboratoryId)
    createOrdersetForm.setFieldValue('provider_id', updatedLaboratoryId)
    editOrdersetForm.setFieldValue('provider_id', updatedLaboratoryId)
  }

  const handleUserSelectedOrdersetId = (updatedUserSelectedOrdersetId: string) => {
    setUserSelectedOrdersetId(updatedUserSelectedOrdersetId)
  }

  const createNewOrderset = async () => {
    const payload = createOrdersetForm.values
    const existingOrdersets = listPartnerProjectPackagesData?.listPartnerProjectPackages?.packages as ProjectPackage[]
    const isNameExisting = existingOrdersets.some((orderset) => orderset.name.toLowerCase() === payload.name.toLowerCase())

    if (!isNameExisting) {
      await createPackage({
        variables: {
          data: {
            ...payload,
            provider_id: userSelectedLaboratoryId,
            partner_price: totalCreateOrdersetClientPrice,
            retail_price: totalCreateOrdersetPatientPrice,
          },
        },
      })
    }
  }

  const updateOrderset = async () => {
    const payload = {
      ...editOrdersetForm.values,
      name: editOrdersetForm?.values.name,
      categories: generateCategoryPanelInput(
        editOrdersetForm?.values?.categories as unknown as PackageCategoryPanel[],
        defaultCategoryData?.configs[0]?.value,
      ),
      partner_price: totalEditOrdersetClientPrice,
      retail_price: totalEditOrdersetPatientPrice,
      provider_id: editOrdersetForm?.values?.provider_id ?? userSelectedLaboratoryId,
    }

    openLoadingModal()

    await updatePackage({
      variables: {
        data: payload,
        id: userSelectedOrdersetId,
      },
    })

    if (!updatePackageLoading) {
      setTimeout(() => {
        closeLoadingModal()
        refetchUserSelectedOrdersetData()
      }, 3000)
      goToOrdersetListPage()
    }
  }

  const deleteOrderset = async () => {
    openLoadingModal()
    await deletePartnerProjectPackage({
      variables: {
        partnerId: me?.partner?._id,
        projectId: me?.project?._id,
        packageId: userSelectedOrdersetId,
      },
    })

    if (!deletePartnerProjectPackageLoading) {
      await deletePackage({
        variables: {
          id: userSelectedOrdersetId,
        },
      })

      if (!deletePackageLoading) {
        setTimeout(() => {
          closeLoadingModal()
        }, 2000)
        goToOrdersetListPage()
      }
    }
  }

  // refetch handlers
  const handleRefetchPackageList = () => {
    refetchPackageList()
  }

  // page redirects
  const goToCreateOrdersetPage = () => {
    push(SETTINGS.newOrderset)
  }

  const goToEditOrdersetPage = (ordersetId: string) => {
    push(SETTINGS.editOrderset.replace(':ordersetId', String(ordersetId)))
  }

  const goToOrdersetListPage = () => {
    createOrdersetForm.reset()
    push(SETTINGS.orderset)
    handleRefetchPackageList()
  }

  // Pricing handlers
  const handleTotalCreateOrdersetPatientClientChange = (updatedPrice: number) => {
    setTotalCreateOrdersetClientPrice(updatedPrice)
  }

  const handleTotalCreateOrdersetPatientPriceChange = (updatedPrice: number) => {
    setTotalCreateOrdersetPatientPrice(updatedPrice)
  }

  const handleTotalEditOrdersetClientPriceChange = (updatedPrice: number) => {
    setTotalEditOrdersetClientPrice(updatedPrice)
  }

  const handleTotalEditOrdersetPatientPriceChange = (updatedPrice: number) => {
    setTotalEditOrdersetPatientPrice(updatedPrice)
  }
  return (
    <OrdersetSettingsPageBusinessLogicProvider
      ordersetList={listPartnerProjectPackagesData?.listPartnerProjectPackages?.packages}
      ordersetListLoading={listPartnerProjectPackagesDataLoading}
      createNewOrderset={createNewOrderset}
      updateOrderset={updateOrderset}
      deleteOrderset={deleteOrderset}
      laboratoryList={providerListForDropdown}
      packageCategories={packageCategoriesDropdownItems}
      compendiumPanels={compendiumPanelDropdownItems}
      defaultCategoryData={defaultCategoryData}
      // Modal
      showLoadingModal={visible}
      openLoadingModal={openLoadingModal}
      closeLoadingModal={closeLoadingModal}
      toggleLoadingModal={toggleLoadingModal}
      // Form
      createOrdersetForm={createOrdersetForm}
      editOrdersetForm={editOrdersetForm}
      // User selections
      userSelectedLaboratoryId={userSelectedLaboratoryId}
      handleUserSelectedLaboratoryIdChange={handleUserSelectedLaboratoryIdChange}
      userSelectedOrdersetId={userSelectedOrdersetId}
      userSelectedOrdersetData={userSelectedOrdersetData}
      userSelectedOrdersetDataLoading={userSelectedOrdersetDataLoading}
      handleUserSelectedOrdersetId={handleUserSelectedOrdersetId}
      // Page redirects
      goToCreateOrdersetPage={goToCreateOrdersetPage}
      goToEditOrdersetPage={goToEditOrdersetPage}
      goToOrdersetListPage={goToOrdersetListPage}
      // Pricing
      totalCreateOrdersetClientPrice={totalCreateOrdersetClientPrice}
      handleTotalCreateOrdersetPatientClientChange={handleTotalCreateOrdersetPatientClientChange}
      totalCreateOrdersetPatientPrice={totalCreateOrdersetPatientPrice}
      handleTotalCreateOrdersetPatientPriceChange={handleTotalCreateOrdersetPatientPriceChange}
      totalEditOrdersetClientPrice={totalEditOrdersetClientPrice}
      handleTotalEditOrdersetClientPriceChange={handleTotalEditOrdersetClientPriceChange}
      totalEditOrdersetPatientPrice={totalEditOrdersetPatientPrice}
      handleTotalEditOrdersetPatientPriceChange={handleTotalEditOrdersetPatientPriceChange}
    >
      {props.children}
    </OrdersetSettingsPageBusinessLogicProvider>
  )
}
